import { useState, useEffect } from 'react';
import styles from '../../Admin/Universidades/Formulario.module.css';
import { Eye, EyeOff } from 'lucide-react';
import Swal from 'sweetalert2';
import { useAuth } from '../../Auth/AuthContext';

const CambiarContrasena = ({ abrirModal, cerrarModal}) => {
    const {userData, getToken} = useAuth(); 
    const usuarioId = userData?.id || userData?.usuario_id; 
    const [formData, setFormData] = useState({
        contrasenia_actual: '',
        nueva_contrasenia: '',
        confirmar_contrasenia: ''
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [showPasswords, setShowPasswords] = useState({
        actual: false,
        nueva: false,
        confirmar: false
    });
    const [isClosing, setIsClosing] = useState(false);

    const token = getToken();

    const validarContrasenia = (password, confirmPassword) => {
        if (!password) return '';
        
        const minLength = 8;
        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);
        const hasNumbers = /\d/.test(password);
        const hasSpecialChar = /[@$!%*?#]/.test(password);
        
        if (password.length < minLength) {
            return 'La contraseña debe tener al menos 8 caracteres';
        }
        if (!hasUpperCase) {
            return 'La contraseña debe tener al menos una letra mayúscula';
        }
        if (!hasLowerCase) {
            return 'La contraseña debe tener al menos una letra minúscula';
        }
        if (!hasNumbers) {
            return 'La contraseña debe tener al menos un número';
        }
        if (!hasSpecialChar) {
            return 'La contraseña debe tener al menos un carácter especial (@$!%*?#)';
        }
        if (confirmPassword && password !== confirmPassword) {
            return 'Las contraseñas no coinciden';
        }
        
        return '';
    };

    const handleCloseModal = () => {
        setIsClosing(true);
    };

    useEffect(() => {
        if (isClosing) {
            const timer = setTimeout(() => {
                setFormData({
                    contrasenia_actual: '',
                    nueva_contrasenia: '',
                    confirmar_contrasenia: ''
                });
                setErrors({});
                setShowPasswords({ actual: false, nueva: false, confirmar: false });
                cerrarModal();
                setIsClosing(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isClosing, cerrarModal]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        if (field === 'nueva_contrasenia') {
            setErrors(prev => ({
                ...prev,
                nueva_contrasenia: validarContrasenia(value, formData.confirmar_contrasenia)
            }));
        } else if (field === 'confirmar_contrasenia') {
            setErrors(prev => ({
                ...prev,
                confirmar_contrasenia: validarContrasenia(formData.nueva_contrasenia, value)
            }));
        } else if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const toggleShowPassword = (field) => {
        setShowPasswords(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.contrasenia_actual) {
            newErrors.contrasenia_actual = 'La contraseña actual es obligatoria';
        }

        const passwordError = validarContrasenia(formData.nueva_contrasenia, formData.confirmar_contrasenia);
        if (passwordError) {
            newErrors.nueva_contrasenia = passwordError;
        }

        if (!formData.confirmar_contrasenia) {
            newErrors.confirmar_contrasenia = 'Confirmar la contraseña es obligatorio';
        } else if (formData.nueva_contrasenia !== formData.confirmar_contrasenia) {
            newErrors.confirmar_contrasenia = 'Las contraseñas no coinciden';
        }

        if (formData.contrasenia_actual === formData.nueva_contrasenia && formData.contrasenia_actual) {
            newErrors.nueva_contrasenia = 'La nueva contraseña debe ser diferente a la actual';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/perfil/${usuarioId}/cambiar-contrasenia`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (!response.ok) {
                setErrors({ general: data.error || 'Error al cambiar la contraseña' });
                return;
            }

            await Swal.fire({
                title: 'Contraseña actualizada correctamente.',
                icon: 'success',
                confirmButtonText: 'Continuar',
                confirmButtonColor: '#242b57',
                customClass: { popup: 'popupbonito' }
            });
            
            handleCloseModal();
        } catch (err) {
            setErrors({ general: 'Error de conexión. Inténtalo de nuevo.' });
        } finally {
            setLoading(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            handleCloseModal();
        }
    };

    if (!abrirModal && !isClosing) return null;

    return (
        <div className={styles.modalAniadir} onClick={handleOverlayClick}>
            <div className={`${styles.modalScrollableAniadir} ${
                abrirModal && !isClosing ? styles.modalAniadirAnim : isClosing ? styles.modalFadeOut : ''
            }`}>
                    <button onClick={handleCloseModal} type="button" className={styles.closeBtnAniadir}>
                        ×
                    </button>
                    <h2 className={styles.tituloAniadir}>Cambiar Contraseña</h2>

                    <form onSubmit={handleSubmit} className={styles.formUsuario}>
                        <div className={styles.campoPassword}>
                            <label htmlFor="contrasenia_actual">
                                Contraseña actual:
                            </label>
                            {errors.general && <p className={styles.mensajeErrorAniadir}>{errors.general}</p>}
                            <div className={styles.contenedorPassword}>
                                <input
                                    id="contrasenia_actual"
                                    type={showPasswords.actual ? "text" : "password"}
                                    value={formData.contrasenia_actual}
                                    onChange={(e) => handleInputChange('contrasenia_actual', e.target.value)}
                                    placeholder="Ingresa tu contraseña actual"
                                    required
                                    disabled={loading}
                                />
                                <button
                                    type="button"
                                    className={styles.botonMostrar}
                                    onClick={() => toggleShowPassword('actual')}
                                    aria-label={showPasswords.actual ? "Ocultar contraseña" : "Mostrar contraseña"}
                                    disabled={loading}
                                >
                                    {showPasswords.actual ? <EyeOff /> : <Eye />}
                                </button>
                            </div>
                            {errors.contrasenia_actual && (
                                <p className={styles.mensajeErrorAniadir}>{errors.contrasenia_actual}</p>
                            )}
                        </div>

                        <div className={styles.campoPassword}>
                            <label htmlFor="nueva_contrasenia">
                                Nueva contraseña:
                            </label>
                            <div className={styles.contenedorPassword}>
                                <input
                                    id="nueva_contrasenia"
                                    type={showPasswords.nueva ? "text" : "password"}
                                    value={formData.nueva_contrasenia}
                                    onChange={(e) => handleInputChange('nueva_contrasenia', e.target.value)}
                                    placeholder="Ingresa tu nueva contraseña"
                                    required
                                    disabled={loading}
                                />
                                <button
                                    type="button"
                                    className={styles.botonMostrar}
                                    onClick={() => toggleShowPassword('nueva')}
                                    aria-label={showPasswords.nueva ? "Ocultar contraseña" : "Mostrar contraseña"}
                                    disabled={loading}
                                >
                                    {showPasswords.nueva ? <EyeOff /> : <Eye />}
                                </button>
                            </div>
                            {errors.nueva_contrasenia && (
                                <p className={styles.mensajeErrorAniadir}>{errors.nueva_contrasenia}</p>
                            )}
                        </div>

                        <div className={styles.campoPasswordFinal}>
                            <label htmlFor="confirmar_contrasenia">
                                Confirmar nueva contraseña:
                            </label>
                            <div className={styles.contenedorPasswordFinal}>
                                <input
                                    id="confirmar_contrasenia"
                                    type={showPasswords.confirmar ? "text" : "password"}
                                    value={formData.confirmar_contrasenia}
                                    onChange={(e) => handleInputChange('confirmar_contrasenia', e.target.value)}
                                    placeholder="Confirma tu nueva contraseña"
                                    required
                                    disabled={loading}
                                />
                                <button
                                    type="button"
                                    className={styles.botonMostrar}
                                    onClick={() => toggleShowPassword('confirmar')}
                                    aria-label={showPasswords.confirmar ? "Ocultar contraseña" : "Mostrar contraseña"}
                                    disabled={loading}
                                >
                                    {showPasswords.confirmar ? <EyeOff /> : <Eye />}
                                </button>
                            </div>
                            {errors.confirmar_contrasenia && (
                                <p className={styles.mensajeErrorAniadir}>{errors.confirmar_contrasenia}</p>
                            )}
                        </div>

                        <button type="submit" className={styles.botonGuard} disabled={loading}>
                            {loading ? 'Cambiando contraseña...' : 'Cambiar contraseña'}
                        </button>
                    </form>
            </div>
        </div>
    );
};

export default CambiarContrasena;